import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLE_LABELS = {
  fortnightly: 'Every 2 Weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual'
};

const BILLING_CYCLE_SAVINGS = {
  fortnightly: null,
  monthly: 'Most Popular',
  quarterly: 'Save 15%',
  annual: 'Save 30%'
};

const SubscriptionPlans = ({ sessionToken, onSubscribe, currentSubscription }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPlanId, setProcessingPlanId] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data.plans || []);
    } catch (err) {
      setError('Failed to load subscription plans');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!sessionToken) {
      alert('Please sign in to subscribe');
      return;
    }

    // Allow subscribing if subscription is set to cancel at period end
    if (currentSubscription && !currentSubscription.cancel_at_period_end) {
      alert('You already have an active subscription. Please cancel it first to switch plans.');
      return;
    }

    setProcessingPlanId(plan.id);

    try {
      const response = await axios.post(
        `${API}/subscriptions/create-checkout`,
        {
          plan_id: plan.id,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to start checkout';
      alert(message);
    } finally {
      setProcessingPlanId(null);
    }
  };

  const getCostPerCredit = (plan) => {
    return (plan.price_usd / plan.credits).toFixed(3);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Subscription Plans
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Choose a plan that fits your needs. Credits roll over to the next billing cycle!
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg border-2 transition-all hover:shadow-xl ${
              plan.is_popular
                ? 'border-blue-500 ring-2 ring-blue-200'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {/* Popular Badge */}
            {plan.is_popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  MOST POPULAR
                </span>
              </div>
            )}

            <div className="p-6">
              {/* Plan Name & Cycle */}
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {plan.name}
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {BILLING_CYCLE_LABELS[plan.billing_cycle]}
                </span>
              </div>

              {/* Price */}
              <div className="text-center mb-4">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  ${plan.price_usd}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  /{plan.billing_cycle === 'fortnightly' ? '2 weeks' : plan.billing_cycle.replace('ly', '')}
                </span>
              </div>

              {/* Credits */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                <div className="text-center">
                  <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {plan.credits.toLocaleString()}
                  </span>
                  <span className="text-gray-600 dark:text-gray-300 ml-2">credits</span>
                </div>
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-1">
                  ${getCostPerCredit(plan)} per credit
                </div>
              </div>

              {/* Discount Badge */}
              {plan.discount_percentage > 0 && (
                <div className="text-center mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    🎉 Save {plan.discount_percentage}%
                  </span>
                </div>
              )}

              {/* Features */}
              {plan.features && plan.features.length > 0 && (
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              )}

              {/* Subscribe Button */}
              {currentSubscription && currentSubscription.plan_id === plan.id && !currentSubscription.cancel_at_period_end ? (
                <button
                  disabled
                  className="w-full py-3 px-4 rounded-lg font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-2 border-green-500 cursor-default"
                >
                  ✓ Current Plan
                </button>
              ) : currentSubscription && currentSubscription.plan_id === plan.id && currentSubscription.cancel_at_period_end ? (
                <button
                  disabled
                  className="w-full py-3 px-4 rounded-lg font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-2 border-yellow-500 cursor-default"
                >
                  ⏳ Ending Soon
                </button>
              ) : currentSubscription && !currentSubscription.cancel_at_period_end ? (
                <button
                  disabled
                  className="w-full py-3 px-4 rounded-lg font-semibold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                >
                  Already Subscribed
                </button>
              ) : (
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={processingPlanId === plan.id}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    plan.is_popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {processingPlanId === plan.id ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : currentSubscription?.cancel_at_period_end ? (
                    'Switch to This Plan'
                  ) : (
                    'Subscribe Now'
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
          ✨ Subscription Benefits
        </h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-800 dark:text-blue-200">
          <li className="flex items-center"><span className="mr-2">✓</span> Unused credits roll over</li>
          <li className="flex items-center"><span className="mr-2">✓</span> Cancel anytime</li>
          <li className="flex items-center"><span className="mr-2">✓</span> Buy additional credits if needed</li>
          <li className="flex items-center"><span className="mr-2">✓</span> Priority support</li>
        </ul>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
